import type { FC } from 'react';
import { Button, CloseIcon, DocumentIcon, Flex, Text } from '@/uicomponents';
import { For } from '@/components/shared/for';
import { Show } from '@/components/shared/show';
import { formatFileSize } from '@/lib/utils';
import type { StagedBriefFile } from '@/hooks';
import styles from './staged-file-pill-list.module.css';

export interface StagedFilePillListProps {
  files: StagedBriefFile[];
  onRemoveFile: (id: string) => void;
  disabled: boolean;
}

/**
 * The staged-file pills, split out of `BriefComposer` (NPJB-369 fix round 2)
 * purely to keep that component's JSX under the house line cap. Rendered as
 * compact inline chips that wrap (NPJB-369 layout fix) rather than the
 * full-width stacked row in `file-picker-dropzone.tsx` — that pattern was
 * built for a single-file empty-state preview, not a multi-file chip list.
 */
export const StagedFilePillList: FC<StagedFilePillListProps> = ({
  files,
  onRemoveFile,
  disabled,
}) => (
  <Show when={files.length > 0}>
    <Flex wrap gap={8}>
      <For
        each={files}
        renderItem={(staged) => (
          <Flex key={staged.id} align='center' gap={8} className={styles.filePill}>
            <DocumentIcon className={styles.fileIcon} />
            <Text weight='semibold' ellipsis className={styles.fileName}>
              {staged.file.name}
            </Text>
            <Text variant='caption' tone='tertiary'>
              {formatFileSize(staged.file.size)}
            </Text>
            <Button
              type='text'
              size='small'
              icon={<CloseIcon />}
              onClick={() => onRemoveFile(staged.id)}
              disabled={disabled}
              aria-label={`Remove ${staged.file.name}`}
            />
          </Flex>
        )}
      />
    </Flex>
  </Show>
);
